import axios from 'axios'
import snakeCaseKeys from 'snakecase-keys'
import camelCaseKeys from 'camelcase-keys-deep'
import Qs from 'qs'
import { toast } from 'vue3-toastify'
import { DEFAULT_ERROR_MESSAGE } from '@/common/constants'

const JSON_TYPE = 'application/json'

// Keys that must survive snake-casing: `_destroy` is Rails' reserved nested-attributes
// delete flag — converting it would strip the leading underscore and silently drop the delete.
export const EXCLUDE_KEYS = ['_destroy']

const api = axios.create()

// CSRF token for Rails
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
if (csrfToken) {
  api.defaults.headers.common['X-CSRF-Token'] = csrfToken
}
api.defaults.headers.common.Accept = 'application/json'

// Transform params to snake_case
api.defaults.paramsSerializer = (params) => {
  return params ?
    Qs.stringify(snakeCaseKeys(params, { deep: true, exclude: EXCLUDE_KEYS }), { arrayFormat: 'brackets' }) :
    params
}

// Transform request body to snake_case (skip FormData / Blob / File — pass binary as-is).
// Serializer picks form-urlencoded by default; per-request `Content-Type: application/json`
// opts into JSON so booleans/nulls/numbers preserve their type across the wire.
api.defaults.transformRequest = [(data, headers) => {
  if (!data || data instanceof FormData || data instanceof Blob || data instanceof ArrayBuffer) return data
  const snakeData = snakeCaseKeys(data, { deep: true, exclude: EXCLUDE_KEYS })
  if (headers?.['Content-Type']?.includes(JSON_TYPE)) return JSON.stringify(snakeData)
  return Qs.stringify(snakeData, { arrayFormat: 'brackets', allowEmptyArrays: true })
}]

// Transform response to camelCase
api.defaults.transformResponse = [(str, headers) => {
  const contentType = headers?.['content-type'] || ''
  if (contentType.includes(JSON_TYPE) && str) {
    try {
      return camelCaseKeys(JSON.parse(str))
    } catch {
      return str
    }
  }
  return str
}]

const ignoreActions = ['posts_calendar', 'extract_exifs', 'exifs', 'changelog/entries', 'changelog/ajax/entries']

// Parity with Pundit's page-level rescue (user_not_authorized) and dead
// sessions: a full load of the dashboard lets Rails flash/redirect properly
// (unauthenticated users bounce on to sign-in from there). Fired once —
// concurrent failing requests must not stack redirects.
let authRedirectFired = false
function redirectToDashboard() {
  if (authRedirectFired) return
  authRedirectFired = true
  window.location.assign('/locations')
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 500) {
        if (!ignoreActions.some((actionName) => error.request.responseURL.includes(actionName))) {
          toast.error(DEFAULT_ERROR_MESSAGE, { autoClose: false })
        }
      } else if (error.response.status === 401) {
        redirectToDashboard()
      }
    }

    return Promise.reject(error)
  }
)

export default api
